/**
 * GuestClaimController — the guest conversation follows the visitor into
 * their account (SCRUM-274/276; spec "Claim and return"). Renders nothing;
 * mounted once beside IdleLogoutController (main.tsx), so it reacts whichever
 * screen produced the member token: password login, email verify, the OAuth
 * handoff, onboarding.
 *
 * With a member token in memory and a guest session in this browser:
 *  - a claim intent under 30 minutes old (an in-app sign-in entry was used:
 *    the limit sheet, a locked feature, «Войти», «Ещё») → claim at once;
 *  - otherwise → phase "prompt": ChatRoute asks «Сохранить разговор, начатый
 *    до входа?» — on a shared device the person signing in may not be the
 *    guest, so only an explicit «Сохранить» claims;
 *  - never while the path is under /admin (ruling P5): the spec turns admins
 *    away at the consumer door, and an admin sign-in must not pull a guest
 *    conversation into the admin account. Sellers may claim. Leaving /admin
 *    runs the decision again. A claim about to start checks the browser's
 *    location as well as the router's (a stale retry toast's action).
 * Claim outcomes (contract; amendments B-3, B-4):
 *  - 200 → pointer and guest token gone, thread list refetched, the
 *    conversation opened at /chat?t=<active_thread_id> when the person is on
 *    the chat, "saved" toast;
 *  - 404 `guest.no_session` → already handled (a sequential re-claim after a
 *    successful one): open the pointer's thread, forget the pointer and
 *    refetch the thread list — never a discard (ruling P10). Had the session
 *    been pruned instead, that thread went with it: `goneThreadId` lets
 *    ChatRoute word its not-found state as "the earlier chat expired";
 *  - 409 → another account owns it: say so and drop it here — pointer,
 *    draft, guest token, the server-side session and this tab's copy of the
 *    conversation (chat store and cached transcript);
 *  - 403 `guest.claim.not_allowed` (amendment B-10: the account has
 *    admin-console access) → the same as the 409, with its own notice:
 *    terminal, never retried, the guest thread never opened;
 *  - 503 `guest.unavailable` (Redis down), network, anything else → phase
 *    "retry": keep the pointer, never discard, try again on the next
 *    member-token, pointer or visibility event, or on «Сохранить». A claim
 *    that has not settled after CLAIM_SETTLE_TIMEOUT_MS is aborted and lands
 *    here too, so a hung request never holds the member on «Сохраняем…».
 *    Reported once: the save prompt's inline line while a prompt is mounted,
 *    a toast with the retry otherwise.
 * A pending decision does not outlive the session: logout, idle logout and a
 * persistent 401 (`endLocalSession` → `resetGuestClaim`) and «Не сохранять»
 * drop it, and a claim still on the wire then has its outcome ignored.
 * It never navigates away from a non-chat screen: onboarding keeps going and
 * the auth flow's return_to=/chat?t=<id> brings the person to the
 * conversation. It also owns the `storage` listener that re-bootstraps a
 * guest tab whose session another tab claimed or discarded (W2.2).
 *
 * Reads only the pointer — never chat text (C3 ruling). The unsent draft is
 * restored by ChatRoute once nothing is pending.
 */
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { create } from "zustand";

import { useChatStore } from "@/features/chat/chat-store";
import {
  THREAD_DETAIL_QUERY_KEY,
  claimGuestSession,
  classifyClaimFailure,
  discardGuestSession,
  syncGuestSessionFromPointer,
  type GuestClaimResult,
} from "@/features/chat/guest/api";
import {
  GUEST_POINTER_KEY,
  clearGuestPointer,
  isClaimIntentFresh,
  readGuestPointer,
  useGuestPointer,
} from "@/features/chat/guest/pointer";
import { threadListQueryKey } from "@/features/chat/useThreadList";
import { useAuthStore } from "@/lib/auth-store";
import i18n from "@/lib/i18n";
import { notify } from "@/lib/notify";

export type GuestClaimPhase = "idle" | "claiming" | "prompt" | "retry";

interface GuestClaimState {
  phase: GuestClaimPhase;
  /**
   * The thread a "gone" (404) claim opened (ruling P10). If it turns out
   * not-found, ChatRoute's not-found slot reads "this guest chat expired";
   * cleared once it loads, or when the session ends.
   */
  goneThreadId: string | null;
  setPhase: (phase: GuestClaimPhase) => void;
}

/** One claim per tab, shared by the controller and ChatRoute's hold. */
export const useGuestClaimStore = create<GuestClaimState>((set) => ({
  phase: "idle",
  goneThreadId: null,
  setPhase: (phase) => set({ phase }),
}));

/**
 * Bumped whenever the pending decision is dropped: a claim already on the
 * wire then lands in a world that moved on, and its outcome is ignored.
 */
let claimGeneration = 0;

/** Session end (logout, idle logout, persistent 401): no pending decision survives. */
export function resetGuestClaim(): void {
  claimGeneration += 1;
  useGuestClaimStore.setState({ phase: "idle", goneThreadId: null });
}

/** ChatRoute has dealt with the "gone" thread (it loaded after all). */
export function clearGoneThread(): void {
  useGuestClaimStore.setState({ goneThreadId: null });
}

/**
 * How long a claim may stay on the wire before the controller gives up on it
 * (W2.6 fix round): a request that never settles must not hold the member on
 * «Сохраняем…» forever. The abort lands in phase "retry" — the conversation
 * is kept and still claimable, and the prompt offers «Сохранить» and «Не
 * сохранять». A claim the server completed meanwhile is safe to retry: the
 * same member gets the same 200 (the claim receipt) or a 404 (B-4).
 */
export const CLAIM_SETTLE_TIMEOUT_MS = 15_000;

/*
 * `THREAD_DETAIL_QUERY_KEY` (guest/api) is dropped from the provider's
 * QueryClient below when the guest conversation is: a disabled query still
 * serves its cached entry, and the guest's own read of the thread survives an
 * SPA sign-in in this tab's QueryClient. `discardGuestSession` drops it from
 * the app's singleton as well (final web fix round, item 2).
 */

interface ClaimDeps {
  navigate: (to: string, options: { replace: boolean }) => void;
  queryClient: QueryClient;
  /** The router location when an outcome lands (not when the claim started). */
  here: () => { pathname: string; threadId: string | null };
}

/** `/` sends a member to /chat; both are "on the chat". */
function onChat(pathname: string): boolean {
  return pathname === "/" || pathname === "/chat";
}

/** Ruling P5: the admin console, its own login door included. */
function isAdminPath(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

/**
 * P5 for a claim about to start: the router's location while the caller is
 * mounted, and the BROWSER's in any case — a retry toast's action outlives
 * the component that started the claim, whose `here()` is blank by then.
 */
function underAdmin(deps: Pick<ClaimDeps, "here">): boolean {
  return (
    isAdminPath(deps.here().pathname) ||
    (typeof window !== "undefined" && isAdminPath(window.location.pathname))
  );
}

const tChat = (key: string): string => i18n.t(key, { ns: "chat" });

/** Fire-and-forget without an unhandled rejection (ruling P12). */
function settle(promise: Promise<unknown>): void {
  promise.catch(() => undefined);
}

/**
 * The claim is over — claimed now, or already (B-4). Forget the guest in this
 * tab and, when the person is on the chat, open the conversation.
 */
export function finishGuestClaim(
  deps: Pick<ClaimDeps, "navigate" | "here">,
  threadId: string | null,
): void {
  clearGuestPointer();
  useAuthStore.getState().clearGuest();
  const { pathname, threadId: open } = deps.here();
  if (threadId !== null && onChat(pathname) && open !== threadId) {
    deps.navigate("/chat?t=" + encodeURIComponent(threadId), { replace: true });
  }
}

/**
 * How many GuestSavePrompts are mounted (final web fix round, item 8). While
 * one is, its inline failure line IS the report of a failed claim — a toast
 * on top said the same thing twice. The toast stays for a claim that fails
 * with no prompt in view (a sign-in screen following its return_to,
 * onboarding, settings), and a prompt that appears later takes the report
 * over: it dismisses that toast as it mounts.
 */
let savePromptsMounted = 0;

/** GuestSavePrompt registers itself (item 8 — one report per failure). */
export function useGuestSavePromptPresence(): void {
  useEffect(() => {
    savePromptsMounted += 1;
    if (useGuestClaimStore.getState().phase === "retry") notify.dismiss("guest-claim");
    return () => {
      savePromptsMounted -= 1;
    };
  }, []);
}

function settleFailure(deps: ClaimDeps, err: unknown, pointerThreadId: string | null): void {
  const { setPhase } = useGuestClaimStore.getState();
  const failure = classifyClaimFailure(err);
  switch (failure) {
    case "gone":
      // B-4: a sequential re-claim after a successful one (or nothing left
      // to claim) — the conversation is where the pointer says. Never a
      // discard (P10): there is no session left to discard, and the 403
      // `auth.registration_required` that also lands here must not end one.
      // The thread moved into the account on that earlier claim, so the
      // sidebar refetches here too (final web fix round, item 11 — B-4 has
      // no "saved" outcome of its own to do it).
      useGuestClaimStore.setState({ goneThreadId: pointerThreadId });
      notify.dismiss("guest-claim");
      settle(deps.queryClient.invalidateQueries({ queryKey: threadListQueryKey() }));
      finishGuestClaim(deps, pointerThreadId);
      setPhase("idle");
      return;
    case "other-account":
    case "not-allowed":
      // 409: another account owns it. 403 `guest.claim.not_allowed`
      // (amendment B-10): this account has admin-console access, and admins
      // never claim — as terminal as the 409, so never "retry", which would
      // re-claim on every token, pointer and visibility event for good. Both
      // drop the conversation here like «Не сохранять» (pointer with its
      // claim intent, draft, guest token, the server-side session), with one
      // notice.
      notify.info(
        tChat(failure === "other-account" ? "guest.claim.alreadyClaimed" : "guest.claim.notAllowed"),
        { id: "guest-claim" },
      );
      settle(discardGuestSession());
      // Never show this member a conversation it cannot have — not now on
      // the chat, and not later from this tab's store or its query cache.
      deps.queryClient.removeQueries({ queryKey: THREAD_DETAIL_QUERY_KEY });
      useChatStore.getState().resetAll();
      if (onChat(deps.here().pathname)) deps.navigate("/chat", { replace: true });
      setPhase("idle");
      return;
    case "retry":
      // B-3: keep everything — the conversation is still claimable. One
      // report (item 8): a mounted save prompt shows the failure inline (an
      // older toast goes), otherwise a toast whose action is the retry.
      if (savePromptsMounted > 0) {
        notify.dismiss("guest-claim");
      } else {
        notify.error(tChat("guest.claim.failed"), {
          id: "guest-claim",
          action: { label: tChat("guest.claim.save"), onClick: () => settle(runClaim(deps)) },
        });
      }
      setPhase("retry");
      return;
  }
}

async function runClaim(deps: ClaimDeps): Promise<void> {
  const { phase, setPhase } = useGuestClaimStore.getState();
  if (phase === "claiming") return;
  if (useAuthStore.getState().accessToken === null) return;
  if (underAdmin(deps)) return;
  const pointer = readGuestPointer();
  if (pointer === null || !pointer.hasSession) {
    setPhase("idle");
    return;
  }
  const generation = claimGeneration;
  setPhase("claiming");
  // A claim that never settles is given up (CLAIM_SETTLE_TIMEOUT_MS): the
  // abort rejects with an AbortError, classified "retry" like a 503.
  const giveUp = new AbortController();
  const timer = setTimeout(() => giveUp.abort(), CLAIM_SETTLE_TIMEOUT_MS);
  let result: GuestClaimResult;
  try {
    result = await claimGuestSession(giveUp.signal);
  } catch (err) {
    if (generation !== claimGeneration) return;
    settleFailure(deps, err, pointer.threadId);
    return;
  } finally {
    clearTimeout(timer);
  }
  if (generation !== claimGeneration) return;
  settle(deps.queryClient.invalidateQueries({ queryKey: threadListQueryKey() }));
  finishGuestClaim(deps, result.active_thread_id ?? result.thread_ids[0] ?? pointer.threadId);
  notify.success(tChat("guest.claim.saved"), { id: "guest-claim" });
  setPhase("idle");
}

function useClaimDeps(): ClaimDeps {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  // The latest router state, read when an outcome LANDS — the person may have
  // moved on meanwhile (a sign-in screen following its return_to).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;
  // A component that started a claim (ChatRoute's «Сохранить») may unmount
  // before the outcome lands: its last location is stale then, so it reads
  // as "not on the chat" — never pull someone back to a screen they left.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return useMemo<ClaimDeps>(
    () => ({
      navigate: (to, options) => {
        void navigateRef.current(to, options);
      },
      queryClient,
      here: () =>
        mountedRef.current
          ? {
              pathname: locationRef.current.pathname,
              threadId: new URLSearchParams(locationRef.current.search).get("t"),
            }
          : { pathname: "", threadId: null },
    }),
    [queryClient],
  );
}

export interface GuestClaimControls {
  phase: GuestClaimPhase;
  /** «Сохранить» — claim now (also the manual retry after a 503). */
  save: () => void;
  /** «Не сохранять» — discard the guest session (the caller starts a new chat). */
  dontSave: () => void;
}

export function useGuestClaim(): GuestClaimControls {
  const deps = useClaimDeps();
  const phase = useGuestClaimStore((s) => s.phase);
  const save = useCallback(() => {
    settle(runClaim(deps));
  }, [deps]);
  const dontSave = useCallback(() => {
    // The person's explicit answer outranks a claim still on the wire.
    claimGeneration += 1;
    useGuestClaimStore.getState().setPhase("idle");
    notify.dismiss("guest-claim");
    // The guest's own read of its thread must not outlive this answer in the
    // tab's query cache (a disabled query would still serve it).
    deps.queryClient.removeQueries({ queryKey: THREAD_DETAIL_QUERY_KEY });
    settle(discardGuestSession());
  }, [deps]);
  return { phase, save, dontSave };
}

export function GuestClaimController(): null {
  const deps = useClaimDeps();
  const accessToken = useAuthStore((s) => s.accessToken);
  const pointer = useGuestPointer();
  const onAdmin = isAdminPath(useLocation().pathname);

  // Another tab claimed or discarded → re-bootstrap this one (W2.2).
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== null && event.key !== GUEST_POINTER_KEY) return;
      settle(syncGuestSessionFromPointer());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // A member token + a guest session in this browser → claim or ask. Runs
  // again on every new member token, every pointer change (the B-3 retry)
  // and on leaving /admin (P5).
  useEffect(() => {
    const { phase, setPhase } = useGuestClaimStore.getState();
    if (pointer === null || !pointer.hasSession) {
      if (phase === "prompt" || phase === "retry") setPhase("idle");
      return;
    }
    if (accessToken === null || onAdmin) return;
    if (phase === "claiming" || phase === "prompt") return;
    if (phase === "retry" || isClaimIntentFresh(pointer.claimIntent)) {
      settle(runClaim(deps));
      return;
    }
    setPhase("prompt");
  }, [accessToken, pointer, onAdmin, deps]);

  // B-3: a claim that could not reach the server retries when the tab returns.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      if (useGuestClaimStore.getState().phase !== "retry") return;
      settle(runClaim(deps));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [deps]);

  return null;
}

export default GuestClaimController;
